import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton, SpecraButton, SpecraCard, SpecraProgress } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { FolderKanban } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageBody, PageHero } from '../../../components/layout';
import { selectProcessingJobs, useProcessingJobsStore } from '../../../lib/processing/jobsStore';
import { EntityTable } from '../_shared/components/EntityTable';
import { programOrgDisplay } from '../_shared/components/GovernmentOrgPicker';
import { EmptyState } from '../_shared/components/PageStates';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { usePrograms } from './hooks';
const columns = [
    {
        // This list is the VP's home: naming a lead is their job, so the programs
        // still waiting on them have to be visible from the scan, not one click in.
        // Spelled out in words under the name rather than as a colour or an icon —
        // same reason the Roles tab spells out platform access.
        key: 'programNameFull',
        header: 'Name',
        render: (p)=>/*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: p.programNameFull
                    }),
                    p.needsProgramLead && /*#__PURE__*/ _jsx("div", {
                        className: "text-xs font-medium text-amber-700 dark:text-amber-300",
                        "data-testid": Testids.deliveryPrograms.needsLeadMarker,
                        children: "Needs program lead"
                    })
                ]
            })
    },
    {
        key: 'contractNumber',
        header: 'Contract #'
    },
    {
        key: 'pricingType',
        header: 'Pricing',
        render: (p)=>programOrgDisplay(p).pricing
    },
    {
        key: 'operationalState',
        header: 'State',
        render: (p)=>programOrgDisplay(p).state
    },
    {
        key: 'customerOrg',
        header: 'Customer office',
        render: (p)=>programOrgDisplay(p).customer
    },
    {
        key: 'contractingOffice',
        header: 'Contracting office',
        render: (p)=>programOrgDisplay(p).contracting
    }
];
/**
 * Shown in place of the empty state while a contract batch is still
 * processing: the program this list is "missing" is being synced
 * server-side, so tell the user that instead of presenting a void.
 */ function ProcessingCard({ percent }) {
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        className: "space-y-3 p-6",
        "data-testid": Testids.deliveryPrograms.processingCard,
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm font-medium",
                children: "Your contract is being processed"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraProgress, {
                        "aria-label": "Contract processing progress",
                        value: percent,
                        className: "h-2 flex-1"
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-xs tabular-nums text-muted-foreground",
                        children: [
                            percent,
                            "%"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "It will appear here automatically when processing completes — no refresh needed."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-2 pt-2",
                children: [
                    0,
                    1,
                    2
                ].map((i)=>/*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-10 w-full"
                    }, i))
            })
        ]
    });
}
export function ProgramsListPage() {
    const tenantId = useTenantId();
    const navigate = useNavigate();
    const { data, isLoading, error, refetch } = usePrograms({
        companyId: tenantId
    });
    const activeJobs = useProcessingJobsStore(selectProcessingJobs);
    const contractJob = activeJobs.find((j)=>j.kind === 'contract-docs');
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.deliveryPrograms.listPage,
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: "Programs",
                    subtitle: "Active program records and everything tied to them"
                }),
                /*#__PURE__*/ _jsx(EntityTable, {
                    data: data?.items ?? [],
                    columns: columns,
                    testid: Testids.deliveryPrograms.table,
                    rowTestid: Testids.deliveryPrograms.row,
                    rowKey: (p)=>p.id,
                    isLoading: isLoading,
                    error: error,
                    onRetry: ()=>void refetch(),
                    onRowClick: (row)=>navigate(`/delivery/programs/${row.id}`),
                    emptyState: contractJob ? /*#__PURE__*/ _jsx(ProcessingCard, {
                        percent: contractJob.percent
                    }) : /*#__PURE__*/ _jsx(EmptyState, {
                        icon: FolderKanban,
                        title: "No programs yet",
                        description: "Programs are created from a contract project. Go to Projects → New Project → Contract to add one.",
                        action: /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            size: "sm",
                            onClick: ()=>navigate('/projects/create'),
                            children: "Go to Projects"
                        })
                    })
                })
            ]
        })
    });
}
